// Per-visitor detail drawer for /admin/analytics. Ported from
// Cambonic_SaaS admin/VisitorDrawer.tsx; translated to Tailwind 3
// arbitrary syntax + dropped the per-finding history (no findings
// concept in GutterInventory).

import { Drawer } from './Drawer.js';
import type { AnalyticsVisitor } from '../../api/admin.js';

interface VisitorDrawerProps {
  visitor: AnalyticsVisitor | null;
  onClose: () => void;
}

export function VisitorDrawer({ visitor, onClose }: VisitorDrawerProps) {
  return (
    <Drawer open={!!visitor} onClose={onClose} width="w-[520px]">
      {visitor && <VisitorDetail v={visitor} onClose={onClose} />}
    </Drawer>
  );
}

function VisitorDetail({ v, onClose }: { v: AnalyticsVisitor; onClose: () => void }) {
  return (
    <>
      <header className="flex items-start justify-between gap-3 px-6 py-4 border-b border-[rgb(var(--color-navy)/0.1)]">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-bronze))] font-semibold">
              Visitor
            </span>
            {v.is_bot && (
              <span className="px-1.5 py-0.5 rounded-[var(--radius-sm)] bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider">
                Bot
              </span>
            )}
          </div>
          <h2 className="font-display text-xl text-[rgb(var(--color-navy))] break-all leading-tight">
            {v.user_email ?? <span className="text-[rgb(var(--color-navy)/0.55)] italic">Anonymous</span>}
          </h2>
          <div className="font-mono text-[11px] text-[rgb(var(--color-navy)/0.55)] mt-1">{v.ip_hash}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close drawer"
          className="shrink-0 w-8 h-8 grid place-items-center rounded-[var(--radius-sm)] text-[rgb(var(--color-navy)/0.55)] hover:text-[rgb(var(--color-navy))] hover:bg-[rgb(var(--color-navy)/0.05)]"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 3l10 10M13 3L3 13" />
          </svg>
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <DetailCell label="Country">
            <CountryDisplay code={v.country} />
          </DetailCell>
          <DetailCell label="Authed user">
            {v.user_email ? (
              <span className="text-sm text-[rgb(var(--color-navy)/0.85)] break-all">{v.user_email}</span>
            ) : (
              <span className="text-[rgb(var(--color-navy)/0.45)] italic text-sm">Anonymous (no session)</span>
            )}
          </DetailCell>
          <DetailCell label="Total events">
            <span className="font-mono text-2xl tabular-nums text-[rgb(var(--color-navy))] font-semibold">
              {v.events.toLocaleString()}
            </span>
          </DetailCell>
          <DetailCell label="Page views">
            <span className="font-mono text-2xl tabular-nums text-[rgb(var(--color-bronze))] font-semibold">
              {v.page_views.toLocaleString()}
            </span>
          </DetailCell>
          <DetailCell label="First seen">
            <span className="text-sm text-[rgb(var(--color-navy)/0.85)]">{formatFullDate(v.first_seen)}</span>
            <div className="text-[11px] text-[rgb(var(--color-navy)/0.5)]">{formatRelative(v.first_seen)}</div>
          </DetailCell>
          <DetailCell label="Last seen">
            <span className="text-sm text-[rgb(var(--color-navy)/0.85)]">{formatFullDate(v.last_seen)}</span>
            <div className="text-[11px] text-[rgb(var(--color-navy)/0.5)]">{formatRelative(v.last_seen)}</div>
          </DetailCell>
        </div>

        <section>
          <h3 className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold mb-2">
            User-Agent
          </h3>
          <div className="font-mono text-[11px] text-[rgb(var(--color-navy)/0.85)] bg-white/60 border border-[rgb(var(--color-navy)/0.1)] rounded-[var(--radius-md)] p-3 break-all leading-relaxed">
            {v.user_agent ?? <span className="text-[rgb(var(--color-navy)/0.45)] italic">Not captured</span>}
          </div>
          {v.user_agent && <UAFingerprint ua={v.user_agent} />}
        </section>

        <section className="rounded-[var(--radius-md)] bg-[rgb(var(--color-navy)/0.04)] border border-[rgb(var(--color-navy)/0.1)] px-4 py-3">
          <h3 className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold mb-1.5">
            Privacy
          </h3>
          <p className="text-[12px] text-[rgb(var(--color-navy)/0.7)] leading-relaxed">
            IPs are hashed (SHA-256, 32 hex chars) so visits group consistently
            without storing the raw address. Country is captured per-event from
            Cloudflare's edge geolocation header. To deanonymize a visitor for
            forensics, correlate the User-Agent + login email above against
            your auth provider's session log.
          </p>
        </section>
      </div>
    </>
  );
}

function DetailCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bg-white/55 border border-[rgb(var(--color-navy)/0.12)] rounded-[var(--radius-md)] px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold mb-1">
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function CountryDisplay({ code }: { code: string | null }) {
  if (!code || code === 'XX') {
    return <span className="text-[rgb(var(--color-navy)/0.45)] italic text-sm">Unknown</span>;
  }
  if (code === 'T1') {
    return (
      <span className="inline-flex items-center gap-2">
        <span className="text-2xl">🧅</span>
        <span className="font-mono text-sm text-[rgb(var(--color-navy)/0.75)]">Tor exit</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-2xl">{flagEmoji(code)}</span>
      <span className="font-mono text-sm text-[rgb(var(--color-navy)/0.85)]">{code}</span>
    </span>
  );
}

function flagEmoji(iso2: string): string {
  if (!/^[A-Z]{2}$/.test(iso2)) return '🏳️';
  const A = 0x1f1e6;
  return String.fromCodePoint(A + iso2.charCodeAt(0) - 65, A + iso2.charCodeAt(1) - 65);
}

function UAFingerprint({ ua }: { ua: string }) {
  const browser = detectBrowser(ua);
  const os = detectOS(ua);
  return (
    <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-wider">
      {browser && <Tag>{browser}</Tag>}
      {os && <Tag>{os}</Tag>}
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-2 py-0.5 rounded-[var(--radius-sm)] bg-[rgb(var(--color-navy)/0.08)] text-[rgb(var(--color-navy)/0.75)] font-semibold">
      {children}
    </span>
  );
}

function detectBrowser(ua: string): string | null {
  if (/edg\//i.test(ua)) return 'Edge';
  if (/firefox/i.test(ua)) return 'Firefox';
  if (/chrome/i.test(ua) && !/edg\//i.test(ua)) return 'Chrome';
  if (/safari/i.test(ua) && !/chrome/i.test(ua)) return 'Safari';
  if (/curl|wget|python|httpclient|go-http/i.test(ua)) return 'CLI';
  if (/bot|crawler|spider/i.test(ua)) return 'Bot';
  return null;
}

function detectOS(ua: string): string | null {
  if (/windows/i.test(ua)) return 'Windows';
  if (/mac os x|macintosh/i.test(ua)) return 'macOS';
  if (/iphone|ipad/i.test(ua)) return 'iOS';
  if (/android/i.test(ua)) return 'Android';
  if (/linux/i.test(ua)) return 'Linux';
  return null;
}

function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}
